import {ICellBuilder, IGrid, IGridDataHolderSortable, IGridDataRow, IRowSortFn} from "/@back@/commons/widgets/grid-core.js";
import {Grid, GridSmall} from "/@back@/commons/widgets/grid-tags.js";
import {DOM, ENodeType, JSX} from "/@lib@/commons/xml/dom.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {IAction} from "/@lib@/commons/actions.js";
import {IReg} from "/@lib@/commons/registry.js";

/**
 * Librairie de ICellBuilder : CellBuilderString, CellBuilderLabel, CellBuilderEnum, CellBuilderIconLabel, CellBuilderNumber, CellBuilderDate
 * Librairie de IGridDataHolder : GridDataHolderJsonArray
 */

/** Construction d'une cellule constituée d'une chaine de caractère. */
export class CellBuilderString implements ICellBuilder {

	protected _comparator: Intl.Collator;

	constructor(public dataKey: string) {}

	setComparator(comparator: Intl.Collator): this {
		this._comparator = comparator;
		return this;
	}


	/** Redessine la cellule dans le container root. */
	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		DOM.setTextContent(root, this._getValue(row) || "\xA0");
	}

	/** Retourne une function de tri **ascendant** associé à cette colonne. */
	getColSortFn(): IRowSortFn {
		return (r1: IGridDataRow, r2: IGridDataRow) => {
			const v1 = this._getValue(r1);
			const v2 = this._getValue(r2);
			if (v1 == null) {
				if (v2 == null) return 0;
				return 1;
			} else if (v2 == null) {
				return -1;
			}
			return this._comparator ? this._comparator.compare(v1, v2) : v1.toString().localeCompare(v2);
		}
	}

	protected _getValue(row: IGridDataRow): any {return row.getData(this.dataKey)}

	override(key: string, val: any): this {
		(this as any)[key] = val;
		return this;
	}
}

/** Construction d'une cellule de type label avec un descriptif en tooltip. */
export class CellBuilderLabel extends CellBuilderString {
	/** class affectée à la balise span portant le background-image. */
	cellClass: string

	setCellClass(cls: string): this {
		this.cellClass = cls;
		return this;
	}

	/** Description longue (usage : tooltip, ...) */
	protected _getDescription(row: IGridDataRow): any {return ""}

	setDescriptionFunc(func: (row: IGridDataRow) => string): any {
		this._getDescription = func;
		return this;
	}

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		super.redrawCell(row, root);
		if (this.cellClass) DOM.addClass(root, this.cellClass);
		DOM.setAttr(root, "title", this._getDescription(row));
	}

}

/** Construction d'une cellule de type flag matérialisée par une icone et un descriptif en tooltip. */
export abstract class CellBuilderFlagIcon extends CellBuilderString {

	/** class affectée à la balise span portant le background-image. */
	iconClass: string = "icon";

	/** largeur disponible pour l'icone. */
	iconWidth: string = "23px";

	/** Filtre appliquée à l'icone. */
	protected iconFilter: string;

	setIconClass(cls: string): this {
		this.iconClass = cls;
		return this;
	}

	setIconWidth(val: string): this {
		this.iconWidth = val;
		return this;
	}

	getIconWidth(row: IGridDataRow): string {
		return this.iconWidth;
	}

	setIconFilter(val: string): this {
		this.iconFilter = val;
		return this;
	}

	getIconFilter(row: IGridDataRow): string {
		return this.iconFilter;
	}


	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		this.redrawIcon(row, root.firstElementChild as HTMLElement || this._buildContent(row, root));
		root.title = this._getValue(row);
	}

	protected redrawIcon(row: IGridDataRow, spanIcon: HTMLElement): void {
		const icon = this._getIcon(row);
		const st = spanIcon.style;
		if (icon) {
			st.backgroundImage = icon.startsWith('--') ? `var(${icon})` : `url("${icon}")`;
			st.filter = this.getIconFilter(row);
		} else {
			st.backgroundImage = 'none';
		}
		st.paddingInlineStart = this.getIconWidth(row);
	}

	/**
	 * Permet de construire la structure stable de la Cellule, quelque-soit la row qui sera ensuite affichée
	 * au fil des appels successifs à redrawCell() (updates des datas, scrolls du grid...).
	 *
	 * Attention : n'utiliser row que pour remonter sur des propriétés stables du contexte :
	 * _buildContent() est appelé uniquement au 1er appel redrawCell(), puis redrawCell()
	 * est appelé avec des valeurs différentes de row.
	 */
	protected _buildContent(row: IGridDataRow, root: HTMLElement): HTMLElement {
		const span = root.appendChild(document.createElement('span'));
		span.classList.add(this.iconClass);
		span.style.unicodeBidi = 'isolate';
		return span;
	}

	/** Peut retourner une url (https:..., data:...) ou une var css (--...). */
	protected abstract _getIcon(row: IGridDataRow): string;
}

/**
 * Cellule avec icone + label.
 * Cette implémentation considère que la valeur retournée par le DataHolder
 * est une url ou une variable css si elle commence par "--".
 */
export class CellBuilderIconLabel extends CellBuilderFlagIcon {
	iconKey: string;
	iconLabelGap: string = ".3em";

	/** Description longue (usage : tooltip, ...) */
	protected _getDescription(row: IGridDataRow): any {return ""}

	setDescriptionFunc(func: (row: IGridDataRow) => string): any {
		this._getDescription = func;
		return this;
	}

	setIconKey(key: string): this {
		this.iconKey = key;
		return this;
	}

	setIconLabelGap(val: string): this {
		this.iconLabelGap = val;
		return this;
	}

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		this.redrawIcon(row, root.firstElementChild as HTMLElement || this._buildContent(row, root));
		const label = this._getValue(row);
		const text = root.lastChild;
		if (text.nodeType !== ENodeType.text) {
			if (label) root.appendChild(document.createTextNode(label));
		} else {
			text.nodeValue = label || "";
		}
		DOM.setAttr(root, "title", this._getDescription(row) || undefined);
	}

	protected _buildContent(row: IGridDataRow, root: HTMLElement): HTMLElement {
		const span = super._buildContent(row, root);
		span.style.marginInlineEnd = this.iconLabelGap;
		return span;
	}

	protected _getIcon(row: IGridDataRow): string {return row.getData(this.iconKey)}
}

/** Affiche une valeur string d'énumération */
export class CellBuilderEnum extends CellBuilderLabel {

	constructor(dataKey: string, public mappingTable: Map<string, string | ICellBuilderEnumEntry>) {
		super(dataKey);
	}

	_getValue(row: IGridDataRow) {
		const key = row.getData(this.dataKey) || "";
		const val = this.mappingTable.get(key);
		if (val === undefined) {
			return key;
		} else if (typeof val === 'string') {
			return val;
		} else if (val) {
			return val.label ? val.label : key;
		}
	}

	protected _getDescription(row: IGridDataRow): any {
		const key = row.getData(this.dataKey) || "";
		const val = this.mappingTable.get(key);
		if (typeof val === 'object')
			return val.description;
	}
}

export type ICellBuilderEnumEntry = {
	label?: string;
	//icon?: string;
	//iconFilter?: string;
	description?: string
}

/** */
export class CellBuilderNumber extends CellBuilderLabel {

	toNumberFunc: (val: any) => number | null;

	defaultSortValue: number = 0;

	constructor(dataKey: string) {
		super(dataKey);
		if (this.cellClass === undefined) this.cellClass = "right";
	}

	fromString(): this {
		this.toNumberFunc = (val: any) => val ? parseInt(val, 10) : null;
		return this;
	}

	getColSortFn(): IRowSortFn {
		if (this.toNumberFunc) return (r1: IGridDataRow, r2: IGridDataRow) => {
			return (this.toNumberFunc(this._getValue(r1)) || this.defaultSortValue) - (this.toNumberFunc(this._getValue(r2)) || this.defaultSortValue);
		};
		return (r1: IGridDataRow, r2: IGridDataRow) => {
			return (this._getValue(r1) || this.defaultSortValue) - (this._getValue(r2) || this.defaultSortValue);
		}
	}
}

/**
 * Cell builder de type "date"
 * La valeur initiale peut être de type integer, ou string (parsée dans ce cas par l'objet Date)
 * Locale par défaut de résolution : fr
 */
export class CellBuilderDate extends CellBuilderNumber {
	toStrFunc: (this: Date, locales?: string | string[], options?: Intl.DateTimeFormatOptions) => string = Date.prototype.toLocaleDateString;

	toNumberFunc = (val: any) => {return Number.isInteger(val) ? val : Date.parse(val)};

	protected _locales: string | string[] = "fr";

	/**
	 * ex :
	 * `{weekday: "long", year: "numeric", month: "long", day: "numeric"}`
	 */
	protected _options: Intl.DateTimeFormatOptions;

	setToStrFunc(func: (this: Date, locales?: string | string[], options?: Intl.DateTimeFormatOptions) => string): this {
		this.toStrFunc = func;
		return this;
	}

	setLocales(locales: string | string[] | null): this {
		this._locales = locales;
		return this;
	}

	setOptions(opts: Intl.DateTimeFormatOptions | null): this {
		this._options = opts;
		return this;
	}

	/** Retourne un objet date ou null si la date n'est pas définie **/
	getDate(row: IGridDataRow): Date | null {
		const d = this.toNumberFunc ? this.toNumberFunc(this._getValue(row)) : this._getValue(row);
		return d > 0 ? new Date(d) : null;
	}

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		const d = this.getDate(row);
		DOM.setTextContent(root, d ? this.toStrFunc.call(d, this._locales, this._options) : "");
		if (this.cellClass)
			DOM.addClass(root, this.cellClass);
		DOM.setAttr(root, "title", this._getDescription(row));
	}

}

/** Construction d'une cellule constituée d'une chaine de caractère. */
export class CellBuilderBarActions<C extends Object> implements ICellBuilder {

	constructor(public id: string, protected actionsLists: IAction<C>[], private actionContextBase: C, protected groupsOrder?: string, protected reg?: IReg<any>) {}

	/** Constructionde l'objet d'initialisation du BarActions */
	buildInitBarActions(row: IGridDataRow): OBarActionsInit<C> {
		return {
			reg: this.reg,
			actions: this.actionsLists,
			groupOrder: this.groupsOrder,
			uiContext: "bar",
			disableFullOverlay: true,
		} as OBarActionsInit<C>;
	}

	/**
	 * Construction de l'actionContext, en affectant la row courante à la propriété "row" de l'actionCOntext de base
	 * défini lors de l'instanciation de ce CellBuilder
	 * Remarque : doit généralement être redéfini via setActionContextMaker
	 */
	protected actionContextUpdater(row: IGridDataRow, actionContext: C): void {
		(actionContext as any)["row"] = [row];
	}

	/** Affectation de la méthode créant l'object "actionsContext" */
	setActionContextMaker(fct: (row: IGridDataRow, actionContext: C) => void): this {
		this.actionContextUpdater = fct;
		return this;
	}

	/** Redessine la cellule dans le container root. */
	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		let barActions: BarActions<C> = root.firstElementChild as BarActions<C>;
		if (!barActions) {
			row.cacheHolder["_" + this.id] = Object.create(this.actionContextBase) as C;
			barActions = root.appendChild(new BarActions<C>().initialize(Object.assign(this.buildInitBarActions(row), {actionContext: row.cacheHolder["_" + this.id]} as OBarActionsInit<C>)));
		}
		this.actionContextUpdater(row, row.cacheHolder["_" + this.id]);
		barActions.refreshContent();
	}

	override(key: string, val: any): this {
		(this as any)[key] = val;
		return this;
	}

	getColSortFn(): IRowSortFn | null {
		return null;
	}
}

/** IGridDataHolder avec en source un tableau d'objets JSON. */
export abstract class GridDataHolderBase<D> implements IGridDataHolderSortable {

	protected _grid: IGrid;
	protected _fakeRow: GridDataRowJson<D>;
	protected r1: GridDataRowJson<D>;
	protected r2: GridDataRowJson<D>;

	protected _datas: D[] = [];
	protected _naturalSortFn: (r1: D, r2: D) => number;
	protected _naturalRowSortFn: IRowSortFn;

	constructor() {
		this._fakeRow = this.newDataRow();
	}

	protected newDataRow(): GridDataRowJson<D> {return new GridDataRowJson(this, null)}

	/** Grid auquel ce IGridDataHolder est rattaché. */
	get grid(): IGrid {return this._grid}

	/** Appelé uniquement par le widget grid. */
	connectToGrid(grid: IGrid | null): void {
		if (this._grid && grid && this._grid !== grid) throw Error("IGridDataHolder already assigned to an other grid.");
		this._grid = grid;
		this._tryBuildDatas();
	}


	/**
	 * A spécifier lorsqu'une fonction de tri naturelle est connue.
	 * Permet une insertion correcte d'entrée, même lorsqu'aucun grid n'est attaché
	 */
	setNaturalOrder(sortFn: null | ((d1: D, d2: D) => number)) {
		this._naturalSortFn = sortFn;
		this._naturalRowSortFn = (r1: GridDataRowJson<D>, r2: GridDataRowJson<D>): number => {
			return this._naturalSortFn(r1.rowDatas, r2.rowDatas);
		}
	}

	/** Initialisation ou remplacement des données en cours. */
	setDatas(datas: D[]): this {
		if (this._grid) {
			const oldLen = this.countRows();
			if (oldLen > 0) {
				//reset propre
				this._datas = [];
				this._grid.rowCountChanged(0, -oldLen);
			} else {
				this._grid.refreshEmptyBody();
			}
		}
		this._datas = datas;
		this._tryBuildDatas();
		return this;
	}

	/** Récupération du tableau données en cours.  */
	getDatas(): D[] {return this._datas as D[]}

	/** Retourne la donnée correspondant à ssa ligne d'affichage dans la grid. */
	getDataByOffset(offset: number): D {
		return this._datas[offset] as D;
	}

	/** Retourne le tableau des lignes sélectionnées dans le grid. */
	getSelectedDatas(): D[] {
		const result: D[] = [];
		if (this._grid) {
			const sels = this._grid.getSelectedRows();
			for (let i = 0; i < sels.length; i++) {
				const offset = sels[i];
				if (offset > 0 || Object.is(offset, +0)) {
					const d = this.getDataByOffset(offset);
					if (d) result.push(d);
				} else {
					//on ajoute une plage depuis l'offset précédent
					const last = -offset;
					for (let o = sels[i - 1] + 1; o <= last; o++) {
						const d = this.getDataByOffset(o);
						if (d) result.push(d);
					}
				}
			}
		}
		return result;
	}

	/** Retourne le tableau des lignes non sélectionnées dans le grid. */
	getUnselectedDatas(): D[] {
		const result: D[] = [];
		const sels = this.grid.getSelectedRows();
		let offsetToTreat = 0;
		for (let i = 0; i < sels.length; i++) {
			const offset = sels[i];
			if (offset > 0 || Object.is(offset, +0)) {
				//on ajoute tout ce qui précède offset.
				for (let o = offsetToTreat; o < offset; o++) result.push(this.getDataByOffset(o));
				offsetToTreat = offset + 1;
			} else {
				offsetToTreat = -offset + 1;
			}
		}
		//on ajoute après la dernière sel.
		for (let o = offsetToTreat, e = this.countRows(); o < e; o++) result.push(this.getDataByOffset(o));
		return result;
	}

	countRows() {return this._datas.length}

	getRow(offset: number): GridDataRowJson<D> {
		const data = this._datas[offset];
		if (!data) return null;
		this._fakeRow.rowDatas = data;
		return this._fakeRow;
	}

	getRowKey(offset: number): D {
		return this._datas[offset] || null;
	}

	getOffset(rowKey: any): number {
		return this._datas.indexOf(rowKey);
	}

	/**
	 * Recherche d'un point d'insertion d'un noeud dans une liste de fils en respectant l'ordre de tri courant.
	 * Si aucun ordre de tri, insère à la fin.
	 */
	findInsertPoint(children: D[], key: D): number {
		//FIXME Si ce holder est détaché du grid, il faudrait mémoriser la fct de tri courante en cas d'insertion pendant cet état détaché.
		const sortFn = this._grid?.sortFn || this.getNaturalSortFn();
		if (!sortFn) return children.length;
		this._initRowDatas();
		const idx = binarySearch(children, key, (a: D, b: D): number => {
			this.r1.rowDatas = a;
			this.r2.rowDatas = b;
			return sortFn(this.r1, this.r2);
		});
		return idx >= 0 ? idx + 1 : -idx - 1;
	}

	protected _initRowDatas() {
		if (this.r1) return;
		this.r1 = this.newDataRow();
		this.r2 = this.newDataRow();
	}

	getNaturalSortFn(): IRowSortFn | null {
		return this._naturalRowSortFn;
	}

	onSortFnChange() {
		//FIXME Si ce holder est détaché du grid, il faudrait mémoriser la fct de tri courante en cas d'insertion pendant cet état détaché.
		const sortFn = this._grid ? this._grid.sortFn : null;
		if (!sortFn) return;

		const activeData = this.getDataByOffset(this._grid.getActiveRow());
		let currentSel: D[];
		let currentNotSel: D[];
		const countRows = this.countRows();
		const countSel = this._grid.countSelectedRows();
		if (countSel < countRows / 2) currentSel = this.getSelectedDatas();
		else currentNotSel = this.getUnselectedDatas();

		this._sortDatas(this._datas, sortFn);

		//redraw du grid
		this._grid.clearSel();
		this._grid.invalidateRows();
		if (currentSel) {
			for (const data of currentSel) {
				const selOffset = this.getOffset(data);
				this._grid.addSelectedRows(selOffset, selOffset);
			}
		} else if (countRows > 0) {
			this._grid.addSelectedRows(0, countRows - 1);
			for (const data of currentNotSel) {
				const selOffset = this.getOffset(data);
				this._grid.removeSelectedRows(selOffset, selOffset);
			}
		}
		if (activeData) {
			const offset = this.getOffset(activeData);
			this._grid.setActiveRow(offset);
			this._grid.ensureRowVisible(offset);
		}
	}

	protected _sortDatas(datas: any[], sortFn: IRowSortFn): void {
		const r1 = this.newDataRow();
		const r2 = this.newDataRow();
		datas.sort((d1: any, d2: any): number => {
			r1.rowDatas = d1;
			r2.rowDatas = d2;
			return sortFn(r1, r2);
		});
	}

	/** initialisation du grid qui DOIT être dans un état vierge.*/
	protected _tryBuildDatas() {
		if (!this._grid) return;
		if (this._datas.length > 0) {
			//tri initial avant la synchro avec le grid.
			const sortFn = this._grid.sortFn;
			if (sortFn) {
				//Tri selon la clé de tri du grid.
				this._sortDatas(this._datas, sortFn);
			}
			this._grid.rowCountChanged(0, this._datas.length);
		}
	}

	rowCache(d: D): any {return (d as any)[cacheSym] || d}

	resetRowCache(d: D): void {
		(d as any)[cacheSym] = Object.create(null);
	}
}

const cacheSym = Symbol("cacheRow");

export class GridDataHolderJsonArray<D> extends GridDataHolderBase<D> {

	public onWillUpdate: EventMgr<(start: number, deleteCount: number, ...insertEntries: D[]) => void>;

	/**
	 * Ajout ou suppression de lignes dans le tableau de données en cours.
	 * L'insertion doit prendre en compte l'ordre de tri courant du grid connecté.
	 */
	updateDatas(start: number, deleteCount: number, ...insertEntries: D[]) {
		if (start < 0 || this._datas.length < start - 1) throw Error("Update insert " + start + " out of bounds 0-" + this._datas.length);
		if (this.onWillUpdate) this.onWillUpdate.emit(start, deleteCount, ...insertEntries);
		this._datas.splice(start, deleteCount, ...insertEntries);
		if (this._grid) this._grid.spliceRows(start, deleteCount, insertEntries ? insertEntries.length : 0);
	}

	setDatas(datas: D[]): this {
		if (this.onWillUpdate) this.onWillUpdate.emit(0, this.countRows(), ...datas);
		return super.setDatas(datas);
	}

	/**
	 * Suppression du ligne par clé
	 */
	deleteRowKey(rowKey: any): boolean {
		const offset = this._datas.indexOf(rowKey);
		if (offset < 0) return false;
		this.updateDatas(offset, 1);
		return true;
	}

	/**
	 * Remplace une ligne par une autre
	 */
	replaceRowKey(currentRowKey: any, newData: D): boolean {
		const offset = this._datas.indexOf(currentRowKey);
		if (offset < 0) return false;
		this.updateDatas(offset, 1, newData);
		return true;
	}
}

/**
 * Mixin pour éviter toute modification des datas D passés à un GridDataHolderBase.
 * Un WeakMap est utilisée pour stocker les propriétés internes au lieu de les ancrer
 * directement sur les datas via des Symbols.
 *
 * Nécessaire dans le cas où les datas D sont mutualisés entre plusieurs grids ou que ces datas
 * sont "frozen", interdisant d'y associer des propriétés supplémentaires.
 */
export function MxGridUntouchedDatas<T extends Constructor<GridDataHolderBase<D>>, D extends Object>(Base: T): T {
	return class MxGridUntouchedDatas extends Base {

		protected intProps = new WeakMap<D, IGridRowInternalProps<D>>();

		protected _saveNaturalOrder(array: any[]) {
			if (!this._naturalSortFn) return;
			for (let i = 0, l = array.length; i < l; i++) {
				const d = array[i];
				let p = this.intProps.get(d);
				if (!p) this.intProps.set(d, (p = Object.create(null)));
				p.natOrder = i;
			}
		}

		protected _naturalSort(this: never, r1: GridDataRowJson<any>, r2: GridDataRowJson<any>): number {
			const intProps = (r1.dataHolder as MxGridUntouchedDatas).intProps;
			return intProps.get(r1.rowDatas).natOrder - intProps.get(r2.rowDatas).natOrder;
		}

		rowCache(d: D): any {
			let p = this.intProps.get(d);
			if (!p) {
				this.intProps.set(d, (p = Object.create(null)));
				p.cache = Object.create(null);
			} else if (!p.cache) {
				p.cache = Object.create(null);
			}
			return p.cache;
		}

		resetRowCache(d: D): void {
			let p = this.intProps.get(d);
			if (p) p.cache = undefined;
		}
	}
}

/** Propriétés internes d'une row pour MxGridUntouchedDatas. */
export interface IGridRowInternalProps<D> {
	cache?: any | undefined
	natOrder?: number
}

/**
 * IGridDataRow trivial fondé sur un objet Json, utilisé pendant le tri
 * et sous forme d'instance singleton pour les cellBuilders.
 */
export class GridDataRowJson<D> implements IGridDataRow {

	constructor(public dataHolder: GridDataHolderBase<D>, public rowDatas: D) {}

	getData(key: string) {return (this.rowDatas as any)[key]}

	get rowKey(): D {return this.rowDatas}

	get cacheHolder() {return this.dataHolder.rowCache(this.rowDatas)}
}


export class GridDataHolderDom extends GridDataHolderJsonArray<Node> {
	protected _fakeRow: GridDataRowDom;

	protected newDataRow(): GridDataRowDom {return new GridDataRowDom(this, null)}
}

export class GridDataRowDom extends GridDataRowJson<Node> {

	getData(key: string): any {
		if (key === '#') return this.rowDatas.textContent;
		if (this.rowDatas instanceof Element) return this.rowDatas.getAttribute(key) || this.rowDatas.querySelector(key);
		return null;
	}
}

/**
 *
 * @return the index of the search key, if it is contained in the list;
 *         otherwise, (- insertionPoint - 1).  The
 *         insertion point is defined as the point at which the
 *         key would be inserted into the list: the index of the first
 *         element greater than the key, or datas.length if all
 *         elements in the list are less than the specified key.  Note
 *         that this guarantees that the return value will be >= 0 if
 *         and only if the key is found.
 */
function binarySearch<E>(datas: E[], key: E, comp: (a: E, b: E) => number): number {
	let low = 0;
	let high = datas.length - 1;
	while (low <= high) {
		const mid = (low + high) >>> 1;
		const midVal = datas[mid];
		const cmp = comp(midVal, key);
		if (cmp < 0)
			low = mid + 1;
		else if (cmp > 0)
			high = mid - 1;
		else
			return mid; // key found
	}
	return -(low + 1);  // key not found
}


//tag déclaré ici pour éviter des pbs d'imports.
customElements.define('c-grid-small', GridSmall);
customElements.define('c-grid', Grid);
